import Phaser from 'phaser';
import { LOCATIONS_VISIT_ORDER, STATE_LOCATION, STATE_WORLD_MAP } from '../constants/AppConstants';
import ScreenContainer from '../display/ui/ScreenContainer';
import Hud from 'app/ui/hud/Hud';
import ScreenOverlayVideo from '../ui/overlays/ScreenOverlayVideo';
import ScreenOverlaySelfie from '../ui/overlays/ScreenOverlaySelfie';
import ScreenOverlayImage from '../ui/overlays/ScreenOverlayImage';
import { GetLocationVideoUrl, GetLocationSouvenirUrl, GetBaseUrl } from '../constants/AppUrls';
import SuitcaseDisplay from '../suitcase/SuitcaseDisplay';
import ScreenOverlayPoll from '../ui/overlays/ScreenOverlayPoll';
import { GetDefaultButtonCostume } from '../ui/hud/HudConstants';
import SuitcasePanels from '../suitcase/SuitcasePanels';

export default class Suitcase extends Phaser.State {
  init({ returnState, locationid = LOCATIONS_VISIT_ORDER[0] } = {}) {
    if (returnState) {
      this.returnState = returnState;
    }

    if (LOCATIONS_VISIT_ORDER.includes(locationid)) {
      this.currentCountry = LOCATIONS_VISIT_ORDER.indexOf(locationid);
      this.jumpToCountry = true;
    } else if (locationid == 'certificate') {
      this.currentCountry = 8;
      this.jumpToCountry = true;
    } else {
      this.currentCountry = 0;
    }
  }

  create() {
    this.container = new ScreenContainer(this.game, { title: this.game.copy.getCopy('suitcase') });
    this.game.add.existing(this.container);

    //Set BG
    this.bg = new SuitcasePanels(this.game, this.currentCountry);
    this.container.add(this.bg);

    // store panels in here, we can use it for tracking 'state'
    this.contentPanels = [];

    // Overlay
    this.bgOverlay = this.game.add.image(0, 0, 'background_overlay');
    this.bgOverlay.anchor.set(0.5);
    this.bgOverlay.x = this.game.defaultWidth * 0.5;
    this.bgOverlay.y = this.game.defaultHeight * 0.5;
    this.container.add(this.bgOverlay);

    // house the panels in their own group for easier tween logic :)
    this.contentContainer = this.game.make.group();
    // implementing tween between locations, so just creating a panel for each location
    this.locationPanels = LOCATIONS_VISIT_ORDER.map((location, index) => {
      const panel = new SuitcaseDisplay(this.game, location);
      panel.x = index * this.game.defaultWidth;
      panel.onActivitySelected.add(this.handleLocationActivity, this);
      this.contentContainer.add(panel);
      this.contentPanels.push(panel);
      return panel;
    });

    // if the player has earned a certficate, we need to add that panel
    if (this.game.gameController.player.getCertificateEarned()) {
      const certPanel = this.createCertificatePanel();
      this.contentPanels.push(certPanel);
      this.contentContainer.add(certPanel);
    }

    this.container.add(this.contentContainer);

    // create and add the hud
    this.hud = new Hud(this.game, Hud.SUITCASE);
    this.game.add.existing(this.hud);

    this.hud.onButtonSelect.add(this.handleHudButton, this);
    // we look up prev/next buttons often so may as well grab them now
    this.btnNext = this.hud.getButton('nextpage');
    this.btnPrev = this.hud.getButton('prevpage');
    this.updateCycleButtons();

    this.showCountry(this.currentCountry, this.jumpToCountry);

    // play music
    this.game.sounds.setBackgroundMusic('music_map', true);
  }

  createCertificatePanel() {
    const panel = this.game.make.group();
    const cert = this.game.make.image(0, 0, 'certificate_thumbnail');
    cert.x = this.game.defaultWidth * 0.5 - cert.width * 0.5;
    cert.y = this.game.defaultHeight * 0.53 - cert.height * 0.5;
    panel.add(cert);
    panel.x = this.game.defaultWidth * LOCATIONS_VISIT_ORDER.length;

    if (!this.game.device.iOS) {
      const downloadbtn = Hud.CreateHudButton(GetDefaultButtonCostume('print'), this.game);
      downloadbtn.x = cert.x + cert.width - downloadbtn.width * 0.5;
      downloadbtn.y = cert.y + cert.height - downloadbtn.height * 0.5;
      downloadbtn.onInputDown.add(this.handleDownloadCertificate, this);
      panel.add(downloadbtn);
    }

    return panel;
  }

  handleDownloadCertificate() {
    // try and download image, if this fails it will open in new window
    let link = document.createElement('a');
    link.setAttribute(
      'href',
      GetBaseUrl('assets/locale/' + this.game.language + '/certificate.jpg')
    );
    link.setAttribute('download', 'certificate.jpg');
    link.setAttribute('target', '_blank');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // This state uses some 'custom functionality' but utilises the hud for default buttons
  // handle the callback basedon button id
  handleHudButton(btnid, btn) {
    switch (btnid) {
      case 'suitcaseback':
        this.handleBackButton();
        break;
      case 'nextpage':
        this.handleNextCountry();
        break;
      case 'prevpage':
        this.handlePrevCountry();
        break;
    }
  }

  // show the currently active country
  showCountry(index, skipTween) {
    // figure out what country we should be in
    this.locationid = LOCATIONS_VISIT_ORDER[index];

    if (this.screenTween) {
      this.screenTween.stop();
      this.game.tweens.remove(this.screenTween);
    }

    if (!skipTween) {
      this.screenTween = this.game.add
        .tween(this.contentContainer)
        .to(
          { x: -this.game.defaultWidth * this.currentCountry },
          500,
          Phaser.Easing.Quadratic.Out,
          true
        );
    } else {
      this.contentContainer.x = -this.game.defaultWidth * this.currentCountry;
    }

    if (this.currentCountry > LOCATIONS_VISIT_ORDER.length - 1) {
      this.container.setTitle(this.game.copy.getCopy('certificate'));
    } else {
      this.container.setTitle(this.game.copy.getCopy('suitcase'));
    }
  }
  updateCycleButtons() {
    this.btnPrev.visible = this.currentCountry > 0;
    this.btnNext.visible = this.currentCountry < this.contentPanels.length - 1;
  }

  /*
  ======================================================================================
    USER ACTIONS
  ======================================================================================
  */

  handleLocationActivity(activityid, locationid) {
    switch (activityid) {
      case SuitcaseDisplay.VIDEO:
        this.handleShowVideo(locationid);
        break;
      case SuitcaseDisplay.POLL:
        this.handleShowPoll(locationid);
        break;
      case SuitcaseDisplay.SELFIE:
        this.handleShowSelfie(locationid);
        break;
      case SuitcaseDisplay.SOUVENIR:
        this.handleShowSouvenir(locationid);
        break;
      case SuitcaseDisplay.FOOD_GAME:
        this.handlePlayFoodGame(locationid);
        break;
      case SuitcaseDisplay.CHASE_GAME:
        this.handlePlayChaseGame(locationid);
        break;
      case SuitcaseDisplay.LEAVE:
        this.handleExitLocation(locationid);
        break;
    }
  }

  handleNextCountry() {
    this.currentCountry = Math.min(this.contentPanels.length - 1, this.currentCountry + 1);
    this.bg.updateBackground(this.currentCountry);
    this.showCountry(this.currentCountry);
    this.updateCycleButtons();
  }
  handlePrevCountry() {
    this.currentCountry = Math.max(0, this.currentCountry - 1);
    this.bg.updateBackground(this.currentCountry);
    this.showCountry(this.currentCountry);
    this.updateCycleButtons();
  }

  handleBackButton() {
    switch (this.returnState) {
      case STATE_WORLD_MAP:
        this.game.gameController.triggerWorldState();
        break;
      case STATE_LOCATION:
        this.game.gameController.triggerCurrentPlayerLocation();
        break;
      // default to welcome state
      default:
        this.game.gameController.triggerWelcome();
        break;
    }
  }

  handlePlayChaseGame(locationid) {
    this.game.sounds.stopBackgroundMusic();

    this.game.gameController.triggerMiniGameChase({
      suitcaselaunched: true,
      locationid: locationid,
    });
  }
  handlePlayFoodGame(locationid) {
    this.game.sounds.stopBackgroundMusic();

    this.game.gameController.triggerMiniGameFood({
      suitcaselaunched: true,
      locationid: locationid,
    });
  }

  handleShowPoll(locationid) {
    this.game.gameController.player.setPollSeen(locationid);
    this.addOverlay(new ScreenOverlayPoll(this.game, { locationid: locationid }));
  }

  handleShowVideo(locationid) {
    this.game.gameController.player.setVideoSeen(locationid);
    this.addOverlay(
      new ScreenOverlayVideo(this.game, {
        videourl: GetLocationVideoUrl(locationid),
        locationid: locationid,
      })
    );
  }

  handleShowSelfie(locationid) {
    this.game.gameController.player.setSelfieSeen(locationid);
    this.addOverlay(new ScreenOverlaySelfie(this.game, { locationid: locationid }));
  }

  handleShowSouvenir(locationid) {
    this.game.gameController.player.setSouvenirSeen(locationid);
    this.addOverlay(
      new ScreenOverlayImage(this.game, {
        imageurl: GetLocationSouvenirUrl(locationid),
      })
    );
  }

  /*
==================================================================================
  ADD / REMOVE OVERLAYS
==================================================================================
*/

  addOverlay(overlay) {
    if (this.overlay) {
      this.handleOverlayClosed();
    }
    this.overlay = overlay;
    this.overlay.onScreenClosed.add(this.handleOverlayClosed, this);
    this.game.add.existing(this.overlay);
  }

  handleOverlayClosed() {
    if (this.overlay) {
      this.overlay.onScreenClosed.remove(this.handleOverlayClosed, this);
      this.overlay.destroy();
      this.overlay = null;
    }
  }

  /*
  ======================================================================================
    BYE BYE
  ======================================================================================
  */

  shutdown() {
    this.handleOverlayClosed();
    this.hud.onButtonSelect.add(this.handleHudButton, this);
    this.hud.destroy();
    this.hud = null;

    this.container.destroy();
    this.container = null;
  }
}



// WEBPACK FOOTER //
// ./js/app/states/Suitcase.js